import { useCallback, useEffect, useState } from 'react';
import { APP_ERROR, type RegisterGuestSessionResponse } from '@hotel-voice/shared';
import { ensureAnonymousUser } from '../services/firebase/auth';
import { registerGuestSession } from '../services/firebase/callables';
import { readGuestLocation, type GuestLocation } from '../utils/params';
import { errorCode, toUserMessage } from '../utils/errors';

/**
 * `phone-required`: the room is valid but this browser has not yet proved a
 * number on its booking — show the popup, and keep showing it until one does.
 */
export type SessionPhase = 'loading' | 'phone-required' | 'ready' | 'error' | 'missing-location';

export interface GuestSessionState {
  phase: SessionPhase;
  location: GuestLocation | null;
  uid: string | null;
  session: RegisterGuestSessionResponse | null;
  errorMessage: string | null;
  retry: () => void;
  /**
   * Submits the number from the popup. Resolves on a match (phase becomes
   * `ready`) and rejects otherwise — a mismatch (`GUEST_NOT_AUTHENTICATED`)
   * and a network failure alike stay in the popup, where the guest can
   * correct the number and try again.
   */
  submitPhone: (phone: string) => Promise<void>;
}

/**
 * Stage 1 bootstrap: sign in anonymously, then ask the server to validate the
 * hotel/room from the URL and open a guest session.
 *
 * Nothing about the hotel is read from Firestore directly — Security Rules deny
 * that to guests. The hotel name and room number come back from the callable,
 * so an anonymous user cannot enumerate the hotel list.
 *
 * The first registration goes up without a phone. A browser that verified
 * earlier (the anonymous uid persists across reloads) is resumed silently;
 * one that has not is answered `GUEST_PHONE_REQUIRED` and shown the popup.
 * The phone itself is never stored on this side — the server's session
 * document is the only record that the check passed.
 */
export function useGuestSession(): GuestSessionState {
  const [phase, setPhase] = useState<SessionPhase>('loading');
  const [location, setLocation] = useState<GuestLocation | null>(null);
  const [uid, setUid] = useState<string | null>(null);
  const [session, setSession] = useState<RegisterGuestSessionResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      setPhase('loading');
      setErrorMessage(null);

      const guestLocation = readGuestLocation();
      if (!guestLocation) {
        if (!cancelled) {
          setLocation(null);
          setPhase('missing-location');
        }
        return;
      }

      if (!cancelled) setLocation(guestLocation);

      try {
        const user = await ensureAnonymousUser();
        if (cancelled) return;
        setUid(user.uid);

        const registered = await registerGuestSession(guestLocation);
        if (cancelled) return;

        setSession(registered);
        setPhase('ready');
      } catch (error) {
        if (cancelled) return;
        if (errorCode(error) === APP_ERROR.GUEST_PHONE_REQUIRED) {
          setPhase('phone-required');
          return;
        }
        setErrorMessage(toUserMessage(error));
        setPhase('error');
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const submitPhone = useCallback(
    async (phone: string) => {
      if (!location) throw new Error('No guest location to verify against');
      const registered = await registerGuestSession({ ...location, phone });
      setSession(registered);
      setPhase('ready');
    },
    [location],
  );

  return { phase, location, uid, session, errorMessage, retry, submitPhone };
}
